import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MENU_MIN_WIDTH = 180;

/**
 * Fixed-position row action menu rendered via portal so it is not clipped
 * by overflow scroll containers (e.g. wide profile tables).
 */
const RowActionMenu = ({ open, anchorEl, onClose, children, minWidth = MENU_MIN_WIDTH }) => {
    const menuRef = useRef(null);
    const onCloseRef = useRef(onClose);
    const [pos, setPos] = useState({ top: 0, left: 0 });

    onCloseRef.current = onClose;

    useEffect(() => {
        if (!open || !anchorEl)
            return;

        const place = () => {
            const rect = anchorEl.getBoundingClientRect();
            const width = minWidth;
            let left = rect.right - width;
            left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
            let top = rect.bottom + 4;
            const estimatedHeight = 120;
            if (top + estimatedHeight > window.innerHeight)
                top = Math.max(8, rect.top - estimatedHeight - 4);
            setPos({ top, left });
        };

        place();
        window.addEventListener("scroll", place, true);
        window.addEventListener("resize", place);
        return () => {
            window.removeEventListener("scroll", place, true);
            window.removeEventListener("resize", place);
        };
    }, [open, anchorEl, minWidth]);

    useEffect(() => {
        if (!open)
            return undefined;

        // Ignore the same gesture that opened the menu, then listen for outside closes.
        let listening = false;
        const armTimer = window.setTimeout(() => {
            listening = true;
        }, 0);

        const isInside = (target) => {
            if (anchorEl && typeof anchorEl.contains === "function" && anchorEl.contains(target))
                return true;
            if (menuRef.current && menuRef.current.contains(target))
                return true;
            return false;
        };

        const handlePointerDown = (e) => {
            if (!listening)
                return;
            if (isInside(e.target))
                return;
            onCloseRef.current?.();
        };

        const handleKeyDown = (e) => {
            if (e.key === "Escape")
                onCloseRef.current?.();
        };

        // Capture phase so we still close even if something stops bubbling.
        document.addEventListener("pointerdown", handlePointerDown, true);
        document.addEventListener("mousedown", handlePointerDown, true);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            window.clearTimeout(armTimer);
            document.removeEventListener("pointerdown", handlePointerDown, true);
            document.removeEventListener("mousedown", handlePointerDown, true);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, anchorEl]);

    if (!open || !anchorEl)
        return null;

    return createPortal(
        <div
            ref={menuRef}
            className="watchlists-row-action-menu"
            style={{
                position: "fixed",
                top: `${pos.top}px`,
                left: `${pos.left}px`,
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                minWidth: `${minWidth}px`,
                zIndex: 100000,
                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            }}
        >
            {children}
        </div>,
        document.body
    );
};

export default RowActionMenu;
